import { Button } from '../ui';
import { Signal, signal } from '@preact/signals';

export interface FilterState {
  period: string;
  tool: string;
  plan: string;
  environment: string;
  dateFrom: string;
  dateTo: string;
}

const PERIODS = [
  { value: '24h', label: 'Últimas 24h' },
  { value: '7d', label: '7 dias' },
  { value: '30d', label: '30 dias' },
  { value: '90d', label: '90 dias' },
  { value: 'custom', label: 'Personalizado' },
];

const TOOLS = [
  { value: '', label: 'Todas as ferramentas' },
  { value: 'chat', label: 'Chat' },
  { value: 'tasks', label: 'Tarefas' },
  { value: 'machines', label: 'Máquinas' },
  { value: 'cloud', label: 'Cloud' },
  { value: 'router', label: 'Router' },
  { value: 'crm', label: 'CRM' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'backlinks', label: 'Backlinks' },
  { value: 'files', label: 'Arquivos' },
  { value: 'wallet', label: 'Carteira' },
  { value: 'plans', label: 'Planos' },
  { value: 'devmode', label: 'Dev Mode' },
];

const ENVIRONMENTS = [
  { value: '', label: 'Todos' },
  { value: 'production', label: 'Produção' },
  { value: 'staging', label: 'Staging' },
];

interface FilterBarProps {
  filters: Signal<FilterState>;
  onApply: () => void;
  showTool?: boolean;
  showEnvironment?: boolean;
}

export function createFilterState(): Signal<FilterState> {
  return signal<FilterState>({
    period: '7d',
    tool: '',
    plan: '',
    environment: '',
    dateFrom: '',
    dateTo: '',
  });
}

export function filtersToParams(f: FilterState): URLSearchParams {
  const p = new URLSearchParams();
  if (f.period !== 'custom') {
    const now = new Date();
    const ms: Record<string, number> = { '24h': 864e5, '7d': 6048e5, '30d': 2592e6, '90d': 7776e6 };
    const from = new Date(now.getTime() - (ms[f.period] || 6048e5));
    p.set('from', from.toISOString().slice(0, 10));
    p.set('to', now.toISOString().slice(0, 10));
  } else {
    if (f.dateFrom) p.set('from', f.dateFrom);
    if (f.dateTo) p.set('to', f.dateTo);
  }
  if (f.tool) p.set('tool', f.tool);
  if (f.environment) p.set('environment', f.environment);
  return p;
}

const selectCls = 'bg-gray-800 border border-gray-700 text-gray-200 rounded-lg px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none';
const inputCls = selectCls;

export function FilterBar({ filters, onApply, showTool = true, showEnvironment = false }: FilterBarProps) {
  const f = filters.value;
  const update = (patch: Partial<FilterState>) => {
    filters.value = { ...filters.value, ...patch };
  };

  return (
    <div class="flex flex-wrap items-center gap-3 p-4 bg-gray-900/50 rounded-xl border border-gray-800 mb-6">
      <select class={selectCls} value={f.period} onChange={(e) => update({ period: (e.target as HTMLSelectElement).value })}>
        {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
      </select>

      {f.period === 'custom' && (
        <>
          <input type="date" class={inputCls} value={f.dateFrom} onChange={(e) => update({ dateFrom: (e.target as HTMLInputElement).value })} />
          <span class="text-gray-500">→</span>
          <input type="date" class={inputCls} value={f.dateTo} onChange={(e) => update({ dateTo: (e.target as HTMLInputElement).value })} />
        </>
      )}

      {showTool && (
        <select class={selectCls} value={f.tool} onChange={(e) => update({ tool: (e.target as HTMLSelectElement).value })}>
          {TOOLS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      )}

      {showEnvironment && (
        <select class={selectCls} value={f.environment} onChange={(e) => update({ environment: (e.target as HTMLSelectElement).value })}>
          {ENVIRONMENTS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
        </select>
      )}

      <Button variant="ghost"
        class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
        onClick={onApply}
      >
        Aplicar
      </Button>
    </div>
  );
}
